import { useAuthState } from '@/auth/AuthProvider';
import { useRouteTitle } from '@/hooks/useRouteTitle';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useCases, type CaseData } from '@/hooks/useCases';
import { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { logEvent } from '@/lib/analytics.safe';
import { FileText, Calendar, ArrowRight, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import ReviewRequestDialog from '@/components/cases/ReviewRequestDialog';

export default function CasesPage() {
  useRouteTitle('Cases');
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: cases, loading, error } = useCases();

  // WO7-P6-1: Consume state from ProPublicProfilePage ({ requestReview: true, proId })
  const navState = location.state as { requestReview?: boolean; proId?: string } | null;
  const [reviewRequestCaseId, setReviewRequestCaseId] = useState<string | null>(null);
  const [reviewRequestCaseTitle, setReviewRequestCaseTitle] = useState<string>('');
  const pendingReviewProId = navState?.requestReview ? navState.proId : null;
  
  // Debug logging for Cypress
  console.log('[CasesPage] Debug:', { user: user?.uid, casesCount: cases?.length, loading, error, authLoading });

  const handleCaseSelect = (caseItem: CaseData) => {
    const caseId = caseItem.id;
    if (!user?.uid) return;

    // WO7-P6-1: If arriving from Pro profile with pending review request, open dialog instead of navigating
    if (pendingReviewProId) {
      setReviewRequestCaseId(caseId);
      setReviewRequestCaseTitle(caseItem.title || 'Untitled Case');
      return;
    }

    // Fire-and-forget: persist the active case in background.
    // Navigation is handled synchronously by the <Link> element — do NOT call
    // navigate() here. A second navigate() call 1-3s later (after the Firestore
    // write resolves) would push a duplicate history entry, re-process the route
    // tree, and race against RequireSrlHasCase's loading state, causing RequireCaseAccess
    // to remount with accessGranted=false and redirect back to /cases.
    const settingsRef = doc(db, 'users', user.uid);
    setDoc(
      settingsRef,
      {
        settings: { currentCaseId: caseId ?? null },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).then(() => {
      logEvent('case_updated', { caseId });
    }).catch((e) => {
      const message = (e as Error)?.message || 'Unable to set the active case.';
      console.error('Failed to persist active case:', message);
      toast.error('Failed to save active case');
    });
  };

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return 'No date';
    const date = timestamp && typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function'
      ? timestamp.toDate() 
      : new Date(timestamp as string | number);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }).format(date);
  };

  // Wait for auth to load before showing empty state
  if (authLoading || loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-orange-600 border-t-transparent"></div>
          <p className="text-slate-400">Loading your cases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h2 className="mb-2 text-lg font-semibold text-red-900">Unable to Load Cases</h2>
          <p className="mb-4 text-sm text-red-700">
            {(error as { code?: string })?.code === 'permission-denied' 
              ? "You don't have access to any cases yet. Create your first case to get started."
              : 'There was an error loading your cases. Please try again or contact support if the problem persists.'}
          </p>
          <button
            onClick={() => navigate('/cases/new')}
            className="rounded-lg bg-orange-600 px-4 py-2 font-semibold text-white shadow-md transition hover:bg-orange-700"
          >
            Create New Case
          </button>
        </div>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-slate-700/50 bg-slate-800/60 p-12 text-center shadow-sm">
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
            <FileText size={32} className="text-orange-600" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-slate-50">No Cases Yet</h2>
          <p className="mb-6 text-slate-400">
            Create your first case to start organizing evidence, tracking deadlines, and managing your legal work.
          </p>
          <button
            onClick={() => navigate('/cases/new')}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-6 py-3 font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-orange-700 hover:shadow-lg"
          >
            <Plus size={20} />
            Create Your First Case
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* WO7-P6-1: Banner shown when arriving from Pro profile to select a case for review */}
      {pendingReviewProId && (
        <div
          data-testid="cases.pending-review-banner"
          className="mb-6 rounded-lg border border-purple-700/40 bg-purple-900/20 px-4 py-3 text-sm text-purple-200"
        >
          Select a case below to submit it for professional review.
        </div>
      )}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-50">Your Cases</h1>
          <p className="mt-1 text-slate-400">Select a case to view details and manage evidence</p>
        </div>
        <button
          onClick={() => navigate('/cases/new')}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2 font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-orange-700 hover:shadow-lg"
        >
          <Plus size={18} />
          New Case
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cases.map((caseItem: CaseData) => (
          <Link
            key={caseItem.id}
            to={pendingReviewProId ? '#' : `/cases/${caseItem.id}`}
            onClick={(e) => {
              if (pendingReviewProId) e.preventDefault();
              handleCaseSelect(caseItem);
            }}
            className="group rounded-lg border border-slate-700/50 bg-slate-800/60 p-6 text-left shadow-md transition hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 block"
          >
            <div className="mb-4 flex items-start justify-between">
              <div className="flex-1">
                <h3 className="mb-1 text-lg font-semibold text-slate-50 group-hover:text-orange-600">
                  {caseItem.title || 'Untitled Case'}
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Calendar size={14} />
                  <span>{formatDate(caseItem.updatedAt || caseItem.createdAt)}</span>
                </div>
              </div>
              <ArrowRight size={20} className="text-slate-500 transition group-hover:translate-x-1 group-hover:text-orange-600" />
            </div>
            {(caseItem.status && caseItem.status !== 'active') ? (
              <span className="inline-block rounded-full bg-slate-700 px-3 py-1 text-xs font-medium text-slate-300">
                {String(caseItem.status)}
              </span>
            ) : null}
          </Link>
        ))}
      </div>

      {/* WO7-P6-1: Review request dialog — opened when user arrives from Pro profile */}
      {reviewRequestCaseId && (
        <ReviewRequestDialog
          caseId={reviewRequestCaseId}
          caseTitle={reviewRequestCaseTitle}
          onClose={() => setReviewRequestCaseId(null)}
          onSuccess={() => {
            setReviewRequestCaseId(null);
            navigate(`/cases/${reviewRequestCaseId}/marketplace`);
          }}
        />
      )}
    </div>
  );
}

